<script setup>
import { computed, defineAsyncComponent, ref, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import MoreActions from './MoreActions.vue';
import AiAgentBadge from './AiAgentBadge.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import ConversationHeaderParticipants from './ConversationHeaderParticipants.vue';
import HeaderAssigneePill from './header/HeaderAssigneePill.vue';
import HeaderWindowPill from './header/HeaderWindowPill.vue';
import HeaderExceptionZone from './header/HeaderExceptionZone.vue';
import HeaderPinnedItems from './header/HeaderPinnedItems.vue';
import { useHeaderPins } from 'dashboard/composables/useHeaderPins';
import HeaderFunnelPill from './header/HeaderFunnelPill.vue';
import ScheduleMessageModal from './bubble/ScheduleMessageModal.vue';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import MobileConversationMenu from './mobile/MobileConversationMenu.vue';
import Modal from 'dashboard/components/Modal.vue';
import { shouldCollapseHeaderLabels } from './header/headerLayout';
import { channelBadgeStyle, translateChannelLabel } from './header/channelMeta';
import { waitingLabelFromMinutes } from 'dashboard/helper/waitingLabel';
import { toggleExclusiveOpen } from 'dashboard/components-next/Conversation/ContextChips/helpers/exclusiveOpen';
import AgendaChip from 'dashboard/components-next/Conversation/ContextChips/AgendaChip.vue';
import TasksChip from 'dashboard/components-next/Conversation/ContextChips/TasksChip.vue';
import TimerChip from 'dashboard/components-next/Conversation/ContextChips/TimerChip.vue';
import LeadContextPanel from 'dashboard/components-next/Conversation/LeadContextPanel.vue';
import OriginChip from 'dashboard/components-next/Conversation/OriginChip.vue';
import {
  buildMobileConversationMenuItems,
  funnelStageFromItem,
  mobileAiStripState,
  shouldShowMobileContextStrip,
} from './mobile/mobileConversationChrome';
import { hasActiveAiAgent } from 'dashboard/helper/aiAgentMode';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { openUnreadConversationList } from 'dashboard/helper/openUnreadConversationList';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useSmallScreen } from 'dashboard/composables/useSmallScreen';
import { useMobileConversationUi } from 'dashboard/composables/useMobileConversationUi';
import { useShortcutDock } from 'dashboard/components-next/dock/useShortcutDock';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { evaluateSLAStatus } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFocusConversationQueue } from 'dashboard/composables/useFocusConversationQueue';
import { workspaceV2Path } from 'dashboard/helper/workspaceV2';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { resolveAgendaReturnPath } from 'dashboard/routes/dashboard/kanban/agenda/agendaReturnPath';
import ConversationAiAgentAPI from 'dashboard/api/conversationAiAgent';
import KanbanAPI from 'dashboard/api/kanban';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const KanbanItemDetailView = defineAsyncComponent(
  () =>
    import(
      'dashboard/routes/dashboard/kanban/components/KanbanItemDetailView.vue'
    )
);

const { t } = useI18n();
const showScheduleMessage = ref(false);
const showMobileMenu = ref(false);
const resolveActionRef = ref(null);
const showEmailActionsModal = ref(false);
const funnelItem = ref(null);
const kanbanCardOpen = ref(false);
const parkedTip = ref(null);

const { isSmallScreen } = useSmallScreen();
const { openMobileContact, requestComposerAction } = useMobileConversationUi();

const { uiSettings: headerUiSettings } = useUISettings();
const { isListHidden, currentWaitingMinutes, finalizeCurrent } =
  useFocusConversationQueue();
const { isWorkspaceV2 } = useWorkspaceV2();
const { addConversation: addConversationToDock } = useShortcutDock();
const router = useRouter();

const waitingLabel = computed(() =>
  waitingLabelFromMinutes(currentWaitingMinutes.value, t)
);
const hiddenHeaderItems = computed(() => {
  const hidden = headerUiSettings.value?.conversation_header_hidden_items;
  return Array.isArray(hidden) ? hidden : [];
});
const isHeaderItemHidden = name => hiddenHeaderItems.value.includes(name);
// Padrão visível: sem preferência salva os dois ícones aparecem no desktop.
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const isCompactHeader = computed(() => shouldCollapseHeaderLabels(width.value));

const wrapDesktopHeader = computed(
  () => isWorkspaceV2.value && width.value > 0 && width.value < 760
);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

// SiteUp: a barra de contexto do lead só existe nas contas com a flag ligada.
// Falha fechando: sem conta ou sem o getter (bootstrap da conta ainda
// esquentando) a barra não aparece, como useKanbanBoardEnabled e o Jornada 360
// no useUISettings.
const isLeadContextEnabled = computed(() => {
  const isEnabled = store.getters['accounts/isFeatureEnabledonAccount'];
  if (typeof isEnabled !== 'function' || !accountId.value) return false;
  return Boolean(isEnabled(accountId.value, FEATURE_FLAGS.LEAD_CONTEXT_PANEL));
});

// SiteUP CRM (Módulo Imobiliária): a ficha do lead entra no menu do celular só
// na conta com o módulo; quem abre o modal é o ResolveAction.
const isLeadSheetEnabled = computed(() => {
  const isEnabled = store.getters['accounts/isFeatureEnabledonAccount'];
  if (typeof isEnabled !== 'function' || !accountId.value) return false;
  return Boolean(isEnabled(accountId.value, FEATURE_FLAGS.REAL_ESTATE_MODULE));
});

// SiteUp V3 (01/09/2026, mockup final-v3v2.html aprovado pelo Lucas): o topo
// vira gestao por excecao. Com a flag ligada, IA, responsavel, funil, tarefas e
// agenda saem da linha e passam a morar no trilho da direita; o topo fica com
// identidade, a zona de excecao e o Resolver. Desligada, nada muda: e assim que
// o rollout acontece conta a conta, sem repetir a F1 do V2, que foi para
// producao sem estrategia de rollout.
const isHeaderV3 = computed(() => {
  const isEnabled = store.getters['accounts/isFeatureEnabledonAccount'];
  if (typeof isEnabled !== 'function' || !accountId.value) return false;
  return Boolean(
    isEnabled(accountId.value, FEATURE_FLAGS.CONVERSATION_HEADER_V3)
  );
});

// A janela de atendimento fechada e a excecao mais grave que existe: sem ela
// nao da nem para responder. Mesma fonte que a pilula da janela usa.
const semResponsavel = computed(() => !currentChat.value?.meta?.assignee?.id);

const janelaFechada = computed(
  () => isHeaderV3.value && currentChat.value?.can_reply === false
);

const showDesktopTasksIcon = computed(
  () =>
    !isSmallScreen.value && !isHeaderV3.value && !isHeaderItemHidden('tasks')
);
const showDesktopAgendaIcon = computed(
  () =>
    !isSmallScreen.value && !isHeaderV3.value && !isHeaderItemHidden('agenda')
);

const chatMetadata = computed(() => props.chat.meta);

const agendaReturnPath = computed(() =>
  isWorkspaceV2.value
    ? resolveAgendaReturnPath(route.query?.agenda_return, accountId.value)
    : null
);
const backButtonLabel = computed(() =>
  agendaReturnPath.value
    ? t('CONVERSATION.HEADER.BACK_TO_AGENDA')
    : t('CONVERSATION.HEADER.BACK_TO_LIST')
);
const backButtonUrl = computed(() => {
  if (agendaReturnPath.value) return agendaReturnPath.value;
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  const listUrl = conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
  return isWorkspaceV2.value ? workspaceV2Path(listUrl) : listUrl;
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

// Sem origem carimbada nao pode sobrar um separador orfao ao lado da caixa.
// Fixar no topo (mockup pin-mockup.html): quem escolhe onde cada modulo mora e
// quem atende. Aqui o cabecalho so pergunta quais estao fixados.
const { fixados, despromover } = useHeaderPins();

// Sem teto de fixados (Lucas, 01/09): mostra quantos couberem. A medida e a
// REAL: renderiza todos, e enquanto o cabecalho transbordar a propria largura
// (scrollWidth > clientWidth), tira o ultimo fixado; ele volta ao trilho pelo
// despromover(). Regra fixa de viewport (1280) nao servia: a lista de conversas
// e o painel comem largura, e em 1440 dois fixados ja nao apareciam (v968).
const maxVisiveis = ref(Infinity);
const fixadosVisiveis = computed(() =>
  fixados.value.slice(0, maxVisiveis.value)
);
const transborda = () => {
  const el = conversationHeader.value;
  return Boolean(el) && el.scrollWidth > el.clientWidth + 1;
};
let ajustando = false;
const ajustarFixados = async () => {
  if (ajustando) return;
  ajustando = true;
  try {
    maxVisiveis.value = fixados.value.length;
    await nextTick();
    while (maxVisiveis.value > 0 && transborda()) {
      maxVisiveis.value -= 1;
      // eslint-disable-next-line no-await-in-loop
      await nextTick();
    }
  } finally {
    ajustando = false;
  }
};
watch([width, fixados], ajustarFixados, { immediate: true });
// O que saiu do topo por falta de espaco volta ao trilho, sem regravar a escolha.
watch(
  [fixados, fixadosVisiveis],
  () =>
    despromover(fixados.value.filter(m => !fixadosVisiveis.value.includes(m))),
  { immediate: true }
);

// Com aviso na tela, o fixado encolhe: a excecao pede decisao agora, o fixado
// e informacao de apoio.
const pinsCompactos = computed(
  () => janelaFechada.value || semResponsavel.value
);

const temOrigem = computed(() =>
  Boolean(currentContact.value?.custom_attributes?.origem_lead)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(
      snoozedUntil
    )}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const channelName = computed(() => translateChannelLabel(inbox.value, t));
const badgeStyle = computed(() => channelBadgeStyle(inbox.value));

const assignedAgent = computed(() => currentChat.value?.meta?.assignee);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const urgencyStripeClass = computed(() => {
  const priority = props.chat?.priority;
  let sla = null;
  const appliedSla = props.chat?.applied_sla;
  if (appliedSla?.id) {
    const status = evaluateSLAStatus({ appliedSla, chat: props.chat });
    if (status?.isSlaMissed) sla = 'high';
    else if (status?.threshold) sla = 'medium';
  }
  if (priority === 'urgent' || sla === 'high') return 'bg-n-ruby-9';
  if (priority === 'high' || sla === 'medium') return 'bg-n-amber-9';
  return '';
});

const openTip = ref(null);
const toggleTip = name => {
  openTip.value = toggleExclusiveOpen(openTip.value, name);
};
const closeTip = () => {
  openTip.value = null;
};

const onAgendaScheduleMessage = () => {
  closeTip();
  showScheduleMessage.value = true;
};

const onDesktopMenuAction = ({ action }) => {
  if (action === 'schedule') {
    showScheduleMessage.value = true;
    return;
  }
  if (
    action === 'tasks' ||
    action === 'agenda' ||
    action === 'timer' ||
    action === 'participants' ||
    action === 'call' ||
    action === 'sla'
  ) {
    openTip.value = action;
  }
};

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

const funnels = computed(
  () =>
    store.getters['funnel/getFunnels']?.filter(funnel => funnel.active) || []
);

const fetchFunnelItem = async () => {
  if (!props.chat?.id) {
    funnelItem.value = null;
    return;
  }
  try {
    const { data } = await KanbanAPI.getItemsByConversation(props.chat.id);
    const list = Array.isArray(data)
      ? data
      : data?.payload || data?.items || data?.kanban_items || [];
    funnelItem.value = list?.length ? list[0] : null;
  } catch {
    funnelItem.value = props.chat?.kanban_items?.[0] || null;
  }
};

watch(
  () => [isSmallScreen.value, props.chat?.id],
  ([small]) => {
    if (small && props.chat?.id) fetchFunnelItem();
  },
  { immediate: true }
);

const showMobileContextStrip = computed(() =>
  shouldShowMobileContextStrip({
    chat: currentChat.value,
    funnelItem: funnelItem.value,
  })
);

const mobileAiState = computed(() => mobileAiStripState(currentChat.value));
const mobileFunnelStage = computed(() =>
  funnelStageFromItem(funnelItem.value, funnels.value)
);

const mobileMenuItems = computed(() =>
  buildMobileConversationMenuItems({
    t,
    hasActiveAi: hasActiveAiAgent(currentChat.value),
    aiPaused: mobileAiState.value === 'paused',
    hasFunnelCard: Boolean(funnelItem.value?.id),
    isResolved:
      currentChat.value?.status === wootConstants.STATUS_TYPE.RESOLVED,
    isSnoozed: isSnoozed.value,
    isPending: currentChat.value?.status === wootConstants.STATUS_TYPE.PENDING,
    muted: Boolean(currentChat.value?.muted),
    canCall: !isHeaderItemHidden('call_button'),
    hasSla: Boolean(hasSlaPolicyId.value),
    hasMessagingWindow: Boolean(inbox.value?.id),
    isListHidden: isListHidden.value,
    statusActionsFirst: isWorkspaceV2.value,
    includeComposerActions: !isWorkspaceV2.value,
    canGenerateLeadSheet: isLeadSheetEnabled.value,
  })
);

const emitComposerAction = action => {
  emitter.emit(BUS_EVENTS.MOBILE_COMPOSER_ACTION, action);
  requestComposerAction(action);
};

const persistAiPaused = async paused => {
  if (!currentChat.value?.id) return;
  try {
    await ConversationAiAgentAPI.update(currentChat.value.id, { paused });
  } catch {
    useAlert(t('CONVERSATION.HEADER.AI_UPDATE_FAILED'));
  }
};

const openParked = name => {
  parkedTip.value = toggleExclusiveOpen(parkedTip.value, name);
};

const onMobileMenuAction = async ({ action }) => {
  showMobileMenu.value = false;

  if (action === 'copy_id') {
    await copyConversationId();
    return;
  }
  if (action === 'change_assignee') {
    openParked('agent');
    return;
  }
  if (action === 'toggle_ai') {
    await persistAiPaused(mobileAiState.value !== 'paused');
    return;
  }
  if (action === 'ai_configure') {
    const assistantId = currentChat.value?.ai_agent_active_assistant?.id;
    if (!assistantId) return;
    router.push({
      name: 'captain_assistants_settings_index',
      params: {
        accountId: accountId.value,
        assistantId,
      },
    });
    return;
  }
  if (action === 'toggle_status' || action === 'mark_pending') {
    resolveActionRef.value?.handleStatusAction(action);
    return;
  }
  if (action === 'lead_sheet') {
    resolveActionRef.value?.openLeadSheet();
    return;
  }
  if (action === 'schedule') {
    showScheduleMessage.value = true;
    return;
  }
  if (
    action === 'tasks' ||
    action === 'labels' ||
    action === 'participants' ||
    action === 'view_contact' ||
    action === 'agenda' ||
    action === 'timer' ||
    action === 'sla' ||
    action === 'window'
  ) {
    openMobileContact();
    return;
  }
  if (action === 'open_kanban_card') {
    if (funnelItem.value?.id) kanbanCardOpen.value = true;
    else openMobileContact();
    return;
  }
  if (action === 'pin_to_dock') {
    addConversationToDock({
      id: currentChat.value.id,
      label: currentChat.value?.meta?.sender?.name || '',
      thumbnail: currentChat.value?.meta?.sender?.thumbnail || '',
    });
    return;
  }
  if (action === 'mark_unread') {
    try {
      await store.dispatch('markMessagesUnread', { id: currentChat.value.id });
      openUnreadConversationList(router, {
        accountId: accountId.value,
        inboxId: route.params.inbox_id,
        label: route.params.label,
        teamId: route.params.teamId,
      });
    } catch {
      useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.MARK_AS_UNREAD_FAILED'));
    }
    return;
  }
  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
    return;
  }
  if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    return;
  }
  if (action === 'send_transcript') {
    showEmailActionsModal.value = true;
    return;
  }
  if (action === 'call') {
    openParked('call');
    return;
  }
  if (action === 'finish_focus') {
    finalizeCurrent();
    return;
  }
  emitComposerAction(action);
};
</script>

<template>
  <div
    ref="conversationHeader"
    data-testid="focus-spot-header"
    class="relative z-10 flex w-full min-w-0 shrink-0 flex-col overflow-visible border-b border-n-weak bg-n-surface-1"
    :class="{ '!bg-n-background text-[15px]': isWorkspaceV2 }"
  >
    <span
      v-if="urgencyStripeClass"
      class="absolute inset-y-0 w-1 ltr:left-0 rtl:right-0"
      :class="urgencyStripeClass"
      aria-hidden="true"
    />

    <template v-if="isSmallScreen">
      <div
        class="flex items-center gap-2 px-3 py-2"
        :class="{ '!gap-3 !px-4 !py-3': isWorkspaceV2 }"
        data-testid="mobile-conversation-header"
      >
        <router-link
          v-if="showBackButton"
          :to="backButtonUrl"
          data-mobile-header-control="back"
          class="flex size-11 shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
          :title="backButtonLabel"
          :aria-label="backButtonLabel"
        >
          <span class="i-ph-arrow-left size-5" aria-hidden="true" />
        </router-link>
        <div class="relative shrink-0">
          <Avatar
            :name="currentContact.name"
            :src="currentContact.thumbnail"
            :size="36"
            :status="currentContact.availability_status"
            hide-offline-status
          />
          <span
            class="absolute -bottom-0.5 -right-1 flex size-4 items-center justify-center rounded-full ring-2 ring-n-background"
            :class="badgeStyle.bgClass"
            :title="channelName"
          >
            <span
              :class="badgeStyle.icon"
              class="size-2.5 text-white"
              aria-hidden="true"
            />
          </span>
        </div>
        <div class="min-w-0 flex-1 leading-tight">
          <p
            class="m-0 truncate text-[15px] font-medium text-n-slate-12"
            :class="{ '!text-[17px]': isWorkspaceV2 }"
            :title="currentContact.name"
          >
            {{ currentContact.name }}
          </p>
          <div class="flex items-center gap-1.5 min-w-0">
            <p
              class="m-0 truncate text-[11.5px] text-n-slate-11"
              :class="{ '!text-[13px]': isWorkspaceV2 }"
            >
              {{ inbox.name }}
            </p>
            <span
              v-if="isHeaderV3 && temOrigem"
              class="shrink-0 text-[11.5px] text-n-slate-11"
              aria-hidden="true"
            >
              ·
            </span>
            <OriginChip :contact="currentContact" :inline="isHeaderV3" />
          </div>
        </div>
        <ResolveAction
          ref="resolveActionRef"
          grouped
          :mobile-menu="isWorkspaceV2"
          :mobile-menu-items="mobileMenuItems"
          class="shrink-0"
          data-mobile-header-control="resolve"
          @mobile-menu-action="onMobileMenuAction"
        />
        <button
          v-if="!isWorkspaceV2"
          type="button"
          data-mobile-header-control="menu"
          data-testid="mobile-conversation-menu-trigger"
          class="flex size-11 shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
          :title="t('CONVERSATION.HEADER.MORE_ACTIONS')"
          :aria-label="t('CONVERSATION.HEADER.MORE_ACTIONS')"
          @click="showMobileMenu = true"
        >
          <span class="i-ph-dots-three-vertical size-5" aria-hidden="true" />
        </button>
      </div>
      <div
        v-if="isHeaderV3"
        class="flex items-center gap-2 px-3 pb-2"
        data-testid="mobile-exception-strip"
      >
        <HeaderPinnedItems
          :chat="chat"
          :inbox="inbox"
          :itens="fixadosVisiveis"
          compacto
        />
        <HeaderExceptionZone
          :chat="chat"
          :inbox="inbox"
          :window-closed="janelaFechada"
          :reservar="!fixadosVisiveis.length"
        />
      </div>
      <div
        v-if="showMobileContextStrip"
        data-testid="mobile-context-strip"
        class="flex items-center gap-3 border-t border-n-weak px-3 py-1.5"
        :aria-label="t('CONVERSATION.HEADER.MOBILE.CONTEXT_STRIP')"
      >
        <span
          v-if="mobileAiState"
          class="inline-flex min-h-5 items-center gap-1.5 text-[12px] font-medium text-n-slate-12"
          data-testid="mobile-context-ai"
        >
          <span
            class="size-2 shrink-0 rounded-full"
            :class="mobileAiState === 'paused' ? 'bg-n-amber-9' : 'bg-n-teal-9'"
            aria-hidden="true"
          />
          {{
            mobileAiState === 'paused'
              ? t('CONVERSATION.HEADER.MOBILE.AI_PAUSED')
              : t('CONVERSATION.HEADER.MOBILE.AI_REPLYING')
          }}
        </span>
        <span
          v-if="mobileFunnelStage"
          class="truncate text-[12px] font-medium text-n-slate-11"
          data-testid="mobile-context-funnel"
        >
          {{
            t('CONVERSATION.HEADER.MOBILE.FUNNEL_STAGE', {
              name: mobileFunnelStage.name,
              index: mobileFunnelStage.index,
              total: mobileFunnelStage.total,
            })
          }}
        </span>
      </div>
    </template>

    <!-- SiteUp: desktop = uma linha ≤60px com estado em pílulas; o resto vai pro ⋮. -->
    <div
      v-else
      data-testid="desktop-conversation-header"
      class="flex h-[56px] max-h-[60px] w-full min-w-0 items-center gap-2 overflow-visible px-4"
      :class="{
        '!h-auto !min-h-[56px] !max-h-none !gap-3 !px-4 !py-1.5':
          isWorkspaceV2 && !wrapDesktopHeader,
        '!h-auto !max-h-none flex-wrap gap-y-1.5 py-1.5': wrapDesktopHeader,
      }"
    >
      <router-link
        v-if="showBackButton"
        :to="backButtonUrl"
        class="flex size-8 shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :title="backButtonLabel"
        :aria-label="backButtonLabel"
      >
        <span class="i-ph-arrow-left size-[18px]" aria-hidden="true" />
      </router-link>

      <div class="relative shrink-0">
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
        />
        <span
          class="absolute -bottom-0.5 -right-1 flex size-3.5 items-center justify-center rounded-full ring-2 ring-n-background"
          :class="badgeStyle.bgClass"
          :title="channelName"
        >
          <span
            :class="badgeStyle.icon"
            class="size-2 text-white"
            aria-hidden="true"
          />
        </span>
      </div>

      <!-- 02/09/2026, Lucas na tela: com 200px a linha "caixa · origem" cortava
           os dois lados ("Whats... · WhatsApp direto · Fale ..."). O bloco cresce
           até 340px em tela larga, e a origem (o que qualifica a pessoa) tem
           prioridade: a caixa cede primeiro, com o nome inteiro no title. -->
      <div
        class="min-w-0 max-w-[200px] md:max-w-[340px] leading-tight"
        :class="{ 'flex-1': wrapDesktopHeader }"
      >
        <p
          class="m-0 truncate text-[14px] font-medium text-n-slate-12"
          :class="{ '!text-[15px]': isWorkspaceV2 }"
          :title="currentContact.name"
        >
          {{ currentContact.name }}
        </p>
        <div class="flex items-center gap-1.5 min-w-0">
          <p
            class="m-0 truncate text-[11.5px] text-n-slate-11"
            :class="{
              'max-w-[45%] shrink': isHeaderV3 && temOrigem,
              '!text-[13px]': isWorkspaceV2,
            }"
            :title="inbox.name"
          >
            {{ inbox.name }}
          </p>
          <span
            v-if="isHeaderV3 && temOrigem"
            class="shrink-0 text-[11.5px] text-n-slate-11"
            aria-hidden="true"
          >
            ·
          </span>
          <OriginChip :contact="currentContact" :inline="isHeaderV3" />
        </div>
        <span
          v-if="!isHMACVerified"
          v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
          class="i-ph-warning size-3.5 shrink-0 text-n-amber-10"
        />
        <span
          v-if="isSnoozed"
          class="block truncate text-[11px] font-medium text-n-amber-10"
        >
          {{ snoozedDisplayText }}
        </span>
      </div>

      <div
        class="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden"
        :class="{
          'order-last basis-full !flex-none flex-wrap !overflow-visible':
            wrapDesktopHeader,
        }"
        data-testid="desktop-header-context"
      >
        <AiAgentBadge
          v-if="!isListHidden && !isHeaderV3"
          :compact="isCompactHeader"
          :open="openTip === 'ai'"
          :inbox-name="inbox.name"
          @toggle="toggleTip('ai')"
          @close="closeTip"
        />
        <HeaderWindowPill
          v-if="!isHeaderV3"
          :chat="currentChat"
          :inbox="inbox"
          :open="openTip === 'window'"
          @toggle="toggleTip('window')"
          @close="closeTip"
        />
        <HeaderAssigneePill
          v-if="!isHeaderV3"
          :conversation-id="chat.id"
          :inbox-id="chat.inbox_id"
          :assignee="assignedAgent"
          :compact="isCompactHeader && !wrapDesktopHeader"
          :open="openTip === 'agent'"
          @toggle="toggleTip('agent')"
          @close="closeTip"
        />
        <HeaderFunnelPill
          v-if="!isHeaderV3"
          :conversation="chat"
          :open="openTip === 'funnel'"
          @toggle="toggleTip('funnel')"
          @close="closeTip"
        />

        <span
          v-if="isListHidden"
          class="shrink-0 text-[12.5px] font-bold tabular-nums text-n-amber-10"
          data-testid="focus-queue-waiting"
        >
          {{ waitingLabel }}
        </span>
        <button
          v-if="isListHidden"
          type="button"
          class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-n-weak px-3 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-queue-finish"
          @click="finalizeCurrent"
        >
          <span class="i-ph-check size-3.5" aria-hidden="true" />
          {{ t('CHAT_LIST.FOCUS_QUEUE.FINISH') }}
        </button>
      </div>

      <!--
        SiteUp: Tarefas e Agenda são ação, não estado. Ícone menor que as
        pílulas; se a largura aperta, as pílulas que cortam (overflow-hidden
        acima). shrink-0 + size-7 (size-6 no compacto) mantém 1 linha ≤60px.
      -->
      <div
        v-if="showDesktopTasksIcon || showDesktopAgendaIcon"
        class="flex shrink-0 items-center gap-0.5"
        data-testid="desktop-header-actions"
      >
        <TasksChip
          v-if="showDesktopTasksIcon"
          :conversation="chat"
          :open="openTip === 'tasks'"
          :compact="isCompactHeader"
          variant="icon"
          data-testid="desktop-header-tasks"
          @toggle="toggleTip('tasks')"
          @close="closeTip"
        />
        <AgendaChip
          v-if="showDesktopAgendaIcon"
          :conversation="chat"
          :open="openTip === 'agenda'"
          :compact="isCompactHeader"
          variant="icon"
          data-testid="desktop-header-agenda"
          @toggle="toggleTip('agenda')"
          @close="closeTip"
          @schedule-message="onAgendaScheduleMessage"
        />
      </div>

      <div class="ml-auto flex shrink-0 items-center gap-1.5">
        <HeaderPinnedItems
          v-if="isHeaderV3"
          :chat="chat"
          :inbox="inbox"
          :itens="fixadosVisiveis"
          :compacto="pinsCompactos"
        />
        <HeaderExceptionZone
          v-if="isHeaderV3"
          :chat="chat"
          :inbox="inbox"
          :window-closed="janelaFechada"
          :reservar="!fixadosVisiveis.length"
        />
        <ResolveAction grouped />
        <MoreActions
          :show-resolve="false"
          relocated-actions
          :hidden-header-items="hiddenHeaderItems"
          @menu-action="onDesktopMenuAction"
        />
      </div>
    </div>

    <!-- SiteUp: âncora de altura zero no fim das linhas do cabeçalho. Os
         popovers estacionados penduram daqui, e não do fim da raiz, que
         agora inclui a barra de contexto do lead. -->
    <div class="relative z-20 h-0 w-full">
      <!-- SiteUp: se o ícone estiver oculto, o ⋮ continua abrindo o mesmo popover. -->
      <div
        v-if="
          !isSmallScreen && isHeaderItemHidden('tasks') && openTip === 'tasks'
        "
        class="absolute right-4 top-0 pt-1"
        data-testid="desktop-parked-tasks"
      >
        <TasksChip
          :conversation="chat"
          open
          @toggle="closeTip"
          @close="closeTip"
        />
      </div>
      <div
        v-if="
          !isSmallScreen && isHeaderItemHidden('agenda') && openTip === 'agenda'
        "
        class="absolute right-4 top-0 pt-1"
        data-testid="desktop-parked-agenda"
      >
        <AgendaChip
          :conversation="chat"
          open
          @toggle="closeTip"
          @close="closeTip"
          @schedule-message="onAgendaScheduleMessage"
        />
      </div>
      <div
        v-if="!isSmallScreen && openTip === 'timer'"
        class="absolute right-4 top-0 pt-1"
        data-testid="desktop-parked-timer"
      >
        <TimerChip
          :conversation="chat"
          open
          @toggle="closeTip"
          @close="closeTip"
        />
      </div>
      <div
        v-if="!isSmallScreen && openTip === 'participants'"
        class="absolute right-4 top-0 pt-1"
        data-testid="desktop-parked-participants"
      >
        <ConversationHeaderParticipants
          :conversation-id="chat.id"
          :assignee="assignedAgent"
          :compact="isCompactHeader && !wrapDesktopHeader"
          hide-assignee
        />
      </div>
      <div
        v-if="!isSmallScreen && openTip === 'call'"
        class="absolute right-4 top-0 pt-1"
        data-testid="desktop-parked-call"
      >
        <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      </div>
      <div
        v-if="!isSmallScreen && openTip === 'sla' && hasSlaPolicyId"
        class="absolute right-4 top-0 pt-1"
        data-testid="desktop-parked-sla"
      >
        <SLACardLabel :chat="chat" show-extended-info :parent-width="width" />
      </div>
    </div>

    <LeadContextPanel v-if="isLeadContextEnabled" :conversation="chat" />
  </div>
  <ScheduleMessageModal
    v-if="showScheduleMessage && chat?.id"
    :show="showScheduleMessage"
    :conversation-id="chat.display_id ?? chat.id"
    @close="showScheduleMessage = false"
  />
  <EmailTranscriptModal
    v-if="showEmailActionsModal"
    :show="showEmailActionsModal"
    :current-chat="currentChat"
    @cancel="showEmailActionsModal = false"
  />
  <MobileConversationMenu
    v-if="showMobileMenu"
    :items="mobileMenuItems"
    @action="onMobileMenuAction"
    @close="showMobileMenu = false"
  />
  <div v-if="isSmallScreen && parkedTip === 'agent'" class="px-3 py-2">
    <HeaderAssigneePill
      :conversation-id="chat.id"
      :inbox-id="chat.inbox_id"
      :assignee="assignedAgent"
      open
      @toggle="parkedTip = null"
      @close="parkedTip = null"
    />
  </div>
  <div v-if="isSmallScreen && parkedTip === 'call'" class="px-3 py-2">
    <ConversationCallButton :inbox="inbox" :chat="currentChat" />
  </div>
  <Modal
    v-model:show="kanbanCardOpen"
    size="full-width"
    :on-close="() => (kanbanCardOpen = false)"
  >
    <div class="max-h-[88dvh] w-full overflow-y-auto bg-n-solid-1">
      <KanbanItemDetailView
        v-if="kanbanCardOpen && funnelItem?.id"
        :item-id="funnelItem.id"
        :sync-query="false"
        @back="kanbanCardOpen = false"
        @item-updated="fetchFunnelItem"
      />
    </div>
  </Modal>
</template>
